import React, { useEffect, useState } from "react";
import WebmasterSidebar from "../../components/WebmasterSidebar";
import { Link, useSearchParams } from "react-router-dom";
import API_BASE_URL from "../../config/apiConfig";
import search from "../../assets/images/search.svg";
import avatar from "../../assets/images/icon-avatar.svg";
import arrowLeft from "../../assets/images/arrow-left.svg";
import arrowRight from "../../assets/images/arrow-right.svg";
import Swal from "sweetalert2";
import ToggleSwitch from "../../components/ToggleSwitch";


interface Medic {
  user_id: string;
  firstname: string;
  lastname: string;
  email: string;
  is_active: boolean;
}

interface PagedResponse {
  items: Medic[];
  total_count: number;
  page: number;
  page_size: number;
}

const MedicsPage: React.FC = () => {
  // Parametrii din URL pentru paginare
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = Number(searchParams.get("page")) || 1;
  
  const [medics, setMedics] = useState<Medic[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const pageSize = 20; // numar de items pe pagina
  const [totalCount, setTotalCount] = useState<number>(0);

  // Stare pentru cautare
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

    // Noua stare pentru toggle inactive (initial, inactive sunt ascunse)
  const [showInactive, setShowInactive] = useState<boolean>(() => {
    return localStorage.getItem("showInactiveTherapists") === "true";
  });
  useEffect(() => {
    localStorage.setItem("showInactiveTherapists", String(showInactive));
  }, [showInactive]);


  // Calculam numarul total de pagini (se foloseste doar cand nu este activa cautarea)
  const totalPages = Math.ceil(totalCount / pageSize);

  // Functie pentru butoanele de paginare
  const getVisiblePages = (totalPages: number): (number | string)[] => {
    if (totalPages <= 6) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    return [1, 2, 3, "...", totalPages - 2, totalPages - 1, totalPages];
  };

  // Fetch-ul pentru medici: daca searchTerm are cel putin 3 caractere se face search,
  // altfel se face fetch paginat.
  useEffect(() => {
    const fetchMedics = async () => {
      setLoading(true);
      try {
        if (searchTerm.length >= 3) {
          // Cautare: endpoint-ul de search (se presupune ca acesta filtreaza dupa nume sau email)
          const response = await fetch(
            `${API_BASE_URL}/users/medics/search?term=${encodeURIComponent(searchTerm)}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          const data: Medic[] = await response.json();
          setMedics(data);
          // In modul search nu folosim paginarea, dar putem seta totalCount la numarul de rezultate
          setTotalCount(data.length);
        } else {
          // Fetch-ul paginat
          const response = await fetch(
            `${API_BASE_URL}/users/medics/paged?page=${currentPage}&page_size=${pageSize}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          const data: PagedResponse = await response.json();
          setMedics(data.items);
          setTotalCount(data.total_count);
          // Actualizeaza URL-ul doar cand nu se face cautare
          setSearchParams({ page: String(currentPage) });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchMedics();
  }, [currentPage, setSearchParams, searchTerm]);

  const handleToggleActive = async (userId: string) => {
    try {
      await fetch(`${API_BASE_URL}/users/medic/${userId}/toggle-active`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setMedics((prev) =>
        prev.map((medic) =>
          medic.user_id === userId
            ? { ...medic, is_active: !medic.is_active }
            : medic
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (userId: string) => {
    const result = await Swal.fire({
      title: "Ești sigur?",
      text: "Vrei să ștergi acest medic?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#6C298B",
      cancelButtonColor: "#dc3545",
      confirmButtonText: "Da, șterge-l!",
      cancelButtonText: "Anulează",
    });

    
    if (result.isConfirmed) {
      try {
        await fetch(`${API_BASE_URL}/users/medic/${userId}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setMedics((prev) =>
          prev.filter((medic) => medic.user_id !== userId)
        );
        Swal.fire("Șters!", "Medicul a fost șters.", "success");
      } catch (error) {
        console.error(error);
        Swal.fire("Eroare!", "Nu s-a putut șterge medicul deoarece are programări alocate.", "error");
      }
    }
  };


  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem("sidebarCollapsed") === "true";
  });

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Handler pentru resetarea cautarii
  const clearSearch = () => {
    setSearchTerm("");
    setSearchOpen(false);
  };

  const displayedMedics = showInactive ? medics : medics.filter(t => t.is_active);


  return (
    <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
      <WebmasterSidebar onCollapseChange={(collapsed) => setIsCollapsed(collapsed)} />

      <div className="page-content">
        <h1 className="page-title">Adaugă și Editează Medici</h1>
        <div className="table-top">
          <div className="page-subtitle">Echipa medicală</div>
          <div className="table-actions">
            <div id="medicsSearch" className="search-container">
              {searchOpen ? (
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Caută medic..."
                  autoFocus
                />
              ) : (
                <img
                  src={search}
                  alt="search"
                  onClick={() => setSearchOpen(true)}
                  style={{ cursor: "pointer" }}
                />
              )}
              {searchOpen && (
                <button onClick={clearSearch} className="clear-search">
                  x
                </button>
              )}
            </div>
            <ToggleSwitch
              checked={showInactive}
              onChange={setShowInactive}
              labelOn="Arată inactivi"
              labelOff="Arată inactivi"
            />
            <Link to="/webmaster/medics/add-medic">
              <button className="primary-btn">Adaugă Medic</button>
            </Link>
          </div>
        </div>
        {loading ? (
          <p>Se încarcă...</p>
        ) : displayedMedics.length === 0 ? (
          <p>Nu există medici înregistrați.</p>
        ) : (
          <div>
            <div className="table-rounded-container">
              <table className="table-rounded">
                <thead>
                  <tr>
                    <th className="column-name">Nume / Prenume</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th className="column-actions">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedMedics.map((medic) => (
                    <tr key={medic.user_id}>
                      <td>
                        <img
                          src={avatar}
                          alt="medic"
                          className="user-icon"
                        />{" "}
                        {medic.firstname} {medic.lastname}
                      </td>
                      <td>{medic.email}</td>
                      <td>
                        <button
                          onClick={() =>
                            handleToggleActive(medic.user_id)
                          }
                          className={
                            medic.is_active ? "active" : "inactive"
                          }
                        >
                          {medic.is_active ? "Activ" : "Inactiv"}{" "}
                          <span className="button-dot">&#x2022;</span>
                        </button>
                      </td>
                      <td className="column-actions">
                        <button
                          onClick={() => handleDelete(medic.user_id)}
                        >
                          Șterge
                        </button>
                        <Link
                          to={`/webmaster/medics/edit-medic/${medic.user_id}`}
                        >
                          <button>Editează</button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Paginare - afișată doar dacă nu se face căutare */}
              {searchTerm.length < 3 && (
                <div className="pagination">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => goToPage(currentPage - 1)}
                  >
                    <img
                      src={arrowLeft}
                      alt="inapoi"
                      className="pagination-left"
                    />{" "}
                    Înapoi
                  </button>
                  <div className="pages">
                    {getVisiblePages(totalPages).map((pageItem, index) => {
                      if (pageItem === "...") {
                        return (
                          <span key={index} className="pagination-ellipsis">
                            {pageItem}
                          </span>
                        );
                      }
                      return (
                        <button
                          key={index}
                          className={
                            currentPage === pageItem ? "active-page" : ""
                          }
                          onClick={() => goToPage(Number(pageItem))}
                        >
                          {pageItem}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => goToPage(currentPage + 1)}
                  >
                    Înainte{" "}
                    <img
                      src={arrowRight}
                      alt="Înainte"
                      className="pagination-right"
                    />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MedicsPage;
